import styled from "styled-components";
import { typographyMixin } from "../../../design-system/tokens/typography";
import { spacing } from "../../../design-system/tokens/spacing";
import { borderRadius } from "../../../design-system/tokens/borders";
import { duration, easing } from "../../../design-system/tokens/transitions";

/** Row wrapper — the whole thing is the click target, so it is a <label>. */
export const Wrapper = styled.label<{ $disabled: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
`;

/**
 * The real <input type="radio"> — visually hidden but kept in the layout so it
 * receives focus, participates in arrow-key roving, and reports to the a11y
 * tree. `Ring` below is purely decorative.
 */
export const HiddenInput = styled.input`
  position: absolute;
  width: 1rem;
  height: 1rem;
  opacity: 0;
  margin: 0;
  cursor: inherit;
`;

export const Control = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  flex: 0 0 auto;
`;

/** 1rem ring; 1px hairline border is the documented px exception. */
export const Ring = styled.span<{ $checked: boolean }>`
  box-sizing: border-box;
  width: 1rem;
  height: 1rem;
  min-width: 1rem;
  min-height: 1rem;
  aspect-ratio: 1;
  border-radius: ${borderRadius.full};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme, $checked }) =>
    $checked ? theme.colors.primary[100] : theme.colors.background.primary};
  border: 1px solid
    ${({ theme, $checked }) =>
      $checked ? theme.colors.primary[500] : theme.colors.border.stroke};
  transition: background-color ${duration.fast} ${easing.smooth}, border-color ${duration.fast} ${easing.smooth};

  /* Focus lives on the hidden input, so mirror it onto the visible ring. */
  ${HiddenInput}:focus-visible + & {
    outline: 0.125rem solid ${({ theme }) => theme.colors.primary[500]};
    outline-offset: 0.125rem;
  }
`;

/** Filled centre dot — Figma 7px inside the 16px ring. */
export const Dot = styled.span<{ $checked: boolean }>`
  width: 0.4375rem;
  height: 0.4375rem;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary[500]};
  opacity: ${({ $checked }) => ($checked ? 1 : 0)};
  transform: scale(${({ $checked }) => ($checked ? 1 : 0.6)});
  transition: opacity ${duration.fast} ${easing.smooth}, transform ${duration.fast} ${easing.smooth};
`;

export const Label = styled.span`
  ${typographyMixin("caption", 3)}
  color: ${({ theme }) => theme.colors.text.primary};
`;
